/**
 * Archery / 射场 (弓箭场) - Archer training ground
 * Features: Green layered roof, THREE prominent archery targets on FRONT facade
 * Size: 3x3 tiles
 * Design: Targets mounted on front wall, visible from isometric camera
 */

import * as THREE from 'three';

export function createArcheryMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color
): void {
  // === Materials ===
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.95 });
  const darkStoneMat = new THREE.MeshStandardMaterial({ color: 0x5a5a5a, roughness: 0.9 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x6d4c3d, roughness: 0.75 });
  const darkWoodMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.8 });
  const frameWoodMat = new THREE.MeshStandardMaterial({ color: 0x2d5a4a, roughness: 0.7 });
  const greenRoofMat = new THREE.MeshStandardMaterial({ color: 0x3d7a4a, roughness: 0.6 });
  const darkGreenMat = new THREE.MeshStandardMaterial({ color: 0x2a5a3a, roughness: 0.5 });
  const lightGreenMat = new THREE.MeshStandardMaterial({ color: 0x4a8a5a, roughness: 0.65 });
  const targetBoardMat = new THREE.MeshStandardMaterial({ color: 0xd4a574, roughness: 0.75 });
  const redMat = new THREE.MeshStandardMaterial({ color: 0xc62828, roughness: 0.6 });
  const orangeRedMat = new THREE.MeshStandardMaterial({ color: 0xe85a3a, roughness: 0.6 });
  const creamMat = new THREE.MeshStandardMaterial({ color: 0xf5f0e0, roughness: 0.65 });
  const goldMat = new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.6, roughness: 0.4 });
  const lanternMat = new THREE.MeshStandardMaterial({
    color: 0xffcc80,
    emissive: 0xff9800,
    emissiveIntensity: 0.5
  });
  const flagMat = new THREE.MeshStandardMaterial({
    color: 0xe85a3a,
    side: THREE.DoubleSide,
    roughness: 0.8
  });

  // === Stone Platform Base ===
  const baseGeom = new THREE.BoxGeometry(2.8, 0.18, 2.6);
  const base = new THREE.Mesh(baseGeom, stoneMat);
  base.position.y = 0.09;
  base.receiveShadow = true;
  base.castShadow = true;
  group.add(base);

  // Platform edge
  const edgeGeom = new THREE.BoxGeometry(2.9, 0.05, 2.7);
  const edge = new THREE.Mesh(edgeGeom, darkStoneMat);
  edge.position.y = 0.20;
  group.add(edge);

  // Front steps
  for (let i = 0; i < 2; i++) {
    const stepGeom = new THREE.BoxGeometry(1.0 - i * 0.1, 0.08, 0.12);
    const step = new THREE.Mesh(stepGeom, stoneMat);
    step.position.set(0, 0.04 + i * 0.06, 1.2 + i * 0.1);
    group.add(step);
  }

  // === WOODEN FLOOR ===
  const floorGeom = new THREE.BoxGeometry(2.4, 0.05, 2.2);
  const floor = new THREE.Mesh(floorGeom, woodMat);
  floor.position.set(0, 0.24, -0.1);
  floor.receiveShadow = true;
  group.add(floor);

  // === FRONT WALL WITH TARGETS (Main visual feature!) ===
  // This wall is at the front, targets mounted on it facing the camera
  const frontWallGeom = new THREE.BoxGeometry(2.4, 1.2, 0.12);
  const frontWall = new THREE.Mesh(frontWallGeom, darkWoodMat);
  frontWall.position.set(0, 0.85, 0.75);
  frontWall.castShadow = true;
  group.add(frontWall);

  // === THREE ARCHERY TARGETS (On front wall, facing camera!) ===
  const createTarget = (x: number, scale: number = 1.0) => {
    const targetZ = 0.88; // In front of wall
    const targetY = 0.85;
    const radius = 0.32 * scale;

    // Target board (circular, facing forward toward camera)
    const boardGeom = new THREE.CylinderGeometry(radius, radius, 0.08, 32);
    const board = new THREE.Mesh(boardGeom, targetBoardMat);
    board.position.set(x, targetY, targetZ);
    board.rotation.x = Math.PI / 2; // Face forward (toward +Z)
    board.castShadow = true;
    group.add(board);

    // Target rings (on front face)
    const ringZ = targetZ + 0.05;

    // Cream outer ring
    const ring1Geom = new THREE.RingGeometry(radius * 0.72, radius * 0.95, 32);
    const ring1 = new THREE.Mesh(ring1Geom, creamMat);
    ring1.position.set(x, targetY, ringZ);
    group.add(ring1);

    // Red/orange middle ring
    const ring2Geom = new THREE.RingGeometry(radius * 0.38, radius * 0.68, 32);
    const ring2 = new THREE.Mesh(ring2Geom, orangeRedMat);
    ring2.position.set(x, targetY, ringZ + 0.01);
    group.add(ring2);

    // Cream inner circle
    const innerGeom = new THREE.CircleGeometry(radius * 0.35, 32);
    const inner = new THREE.Mesh(innerGeom, creamMat);
    inner.position.set(x, targetY, ringZ + 0.02);
    group.add(inner);

    // Red bullseye center
    const bullseyeGeom = new THREE.CircleGeometry(radius * 0.12, 16);
    const bullseye = new THREE.Mesh(bullseyeGeom, redMat);
    bullseye.position.set(x, targetY, ringZ + 0.03);
    group.add(bullseye);
  };

  // Three targets on front wall
  createTarget(-0.7, 1.0);
  createTarget(0, 1.0);
  createTarget(0.7, 1.0);

  // === BACK WALL ===
  const backWallGeom = new THREE.BoxGeometry(2.4, 1.2, 0.12);
  const backWall = new THREE.Mesh(backWallGeom, darkWoodMat);
  backWall.position.set(0, 0.85, -1.05);
  backWall.castShadow = true;
  group.add(backWall);

  // === SIDE WALLS ===
  const sideWallGeom = new THREE.BoxGeometry(0.1, 1.2, 1.7);
  const leftWall = new THREE.Mesh(sideWallGeom, darkWoodMat);
  leftWall.position.set(-1.15, 0.85, -0.15);
  leftWall.castShadow = true;
  group.add(leftWall);

  const rightWall = new THREE.Mesh(sideWallGeom.clone(), darkWoodMat);
  rightWall.position.set(1.15, 0.85, -0.15);
  rightWall.castShadow = true;
  group.add(rightWall);

  // === CORNER PILLARS (decorative, teal-green) ===
  const pillarGeom = new THREE.BoxGeometry(0.12, 1.3, 0.12);
  const pillarPositions = [
    { x: -1.1, z: 0.7 },
    { x: 1.1, z: 0.7 },
    { x: -1.1, z: -1.0 },
    { x: 1.1, z: -1.0 },
  ];
  for (const pos of pillarPositions) {
    const pillar = new THREE.Mesh(pillarGeom, frameWoodMat);
    pillar.position.set(pos.x, 0.9, pos.z);
    pillar.castShadow = true;
    group.add(pillar);

    // Pillar base
    const pbaseGeom = new THREE.BoxGeometry(0.16, 0.08, 0.16);
    const pillarBase = new THREE.Mesh(pbaseGeom, stoneMat);
    pillarBase.position.set(pos.x, 0.26, pos.z);
    group.add(pillarBase);

    // Pillar cap
    const capGeom = new THREE.BoxGeometry(0.16, 0.04, 0.16);
    const cap = new THREE.Mesh(capGeom, darkGreenMat);
    cap.position.set(pos.x, 1.57, pos.z);
    group.add(cap);
  }

  // === Horizontal Beams ===
  const frontBeamGeom = new THREE.BoxGeometry(2.35, 0.08, 0.08);
  const frontBeam = new THREE.Mesh(frontBeamGeom, frameWoodMat);
  frontBeam.position.set(0, 1.5, 0.7);
  group.add(frontBeam);

  const backBeamGeom = new THREE.BoxGeometry(2.35, 0.08, 0.08);
  const backBeam = new THREE.Mesh(backBeamGeom, frameWoodMat);
  backBeam.position.set(0, 1.5, -1.0);
  group.add(backBeam);

  // === GREEN LAYERED ROOF ===
  // Eave layer (widest)
  const eave1Geom = new THREE.BoxGeometry(3.0, 0.07, 2.5);
  const eave1 = new THREE.Mesh(eave1Geom, darkGreenMat);
  eave1.position.set(0, 1.6, -0.15);
  group.add(eave1);

  // Roof layer 1
  const roof1Geom = new THREE.BoxGeometry(2.7, 0.1, 2.2);
  const roof1 = new THREE.Mesh(roof1Geom, greenRoofMat);
  roof1.position.set(0, 1.7, -0.15);
  roof1.castShadow = true;
  group.add(roof1);

  // Roof layer 2
  const roof2Geom = new THREE.BoxGeometry(2.2, 0.1, 1.8);
  const roof2 = new THREE.Mesh(roof2Geom, greenRoofMat);
  roof2.position.set(0, 1.82, -0.15);
  roof2.castShadow = true;
  group.add(roof2);

  // Roof layer 3
  const roof3Geom = new THREE.BoxGeometry(1.6, 0.1, 1.3);
  const roof3 = new THREE.Mesh(roof3Geom, greenRoofMat);
  roof3.position.set(0, 1.94, -0.15);
  roof3.castShadow = true;
  group.add(roof3);

  // Roof peak
  const peakGeom = new THREE.ConeGeometry(0.65, 0.28, 4);
  const peak = new THREE.Mesh(peakGeom, greenRoofMat);
  peak.position.set(0, 2.12, -0.15);
  peak.rotation.y = Math.PI / 4;
  peak.castShadow = true;
  group.add(peak);

  // Gold ornament
  const ornamentGeom = new THREE.SphereGeometry(0.06, 8, 6);
  const ornament = new THREE.Mesh(ornamentGeom, goldMat);
  ornament.position.set(0, 2.3, -0.15);
  group.add(ornament);

  // === Upturned Eave Corners ===
  const createEaveCorner = (x: number, z: number, rotY: number) => {
    const cornerGeom = new THREE.BoxGeometry(0.35, 0.06, 0.12);
    const corner = new THREE.Mesh(cornerGeom, darkGreenMat);
    corner.position.set(x, 1.62, z);
    corner.rotation.y = rotY;
    corner.rotation.z = -0.25;
    group.add(corner);

    const tipGeom = new THREE.BoxGeometry(0.12, 0.05, 0.08);
    const tip = new THREE.Mesh(tipGeom, lightGreenMat);
    tip.position.set(x * 1.1, 1.66, z * 1.05);
    tip.rotation.y = rotY;
    tip.rotation.z = -0.4;
    group.add(tip);
  };

  createEaveCorner(1.42, 1.1, -Math.PI / 4);
  createEaveCorner(-1.42, 1.1, Math.PI / 4);
  createEaveCorner(1.42, -1.35, Math.PI / 4);
  createEaveCorner(-1.42, -1.35, -Math.PI / 4);

  // === RED FLAG (On pole, visible from isometric view) ===
  const flagPoleX = 1.55;
  const flagPoleZ = 0.6;

  // Flag pole
  const poleGeom = new THREE.CylinderGeometry(0.025, 0.035, 2.4, 6);
  const pole = new THREE.Mesh(poleGeom, woodMat);
  pole.position.set(flagPoleX, 1.5, flagPoleZ);
  pole.castShadow = true;
  group.add(pole);

  // Pole top
  const poleTopGeom = new THREE.SphereGeometry(0.045, 6, 4);
  const poleTop = new THREE.Mesh(poleTopGeom, goldMat);
  poleTop.position.set(flagPoleX, 2.75, flagPoleZ);
  group.add(poleTop);

  // Flag - simple plane, tilted forward for visibility
  const flagGeom = new THREE.PlaneGeometry(0.6, 0.45);
  // Add wave effect
  const flagPositions = flagGeom.attributes.position;
  for (let i = 0; i < flagPositions.count; i++) {
    const x = flagPositions.getX(i);
    flagPositions.setZ(i, Math.sin(x * 4) * 0.04);
  }
  flagGeom.computeVertexNormals();

  const flag = new THREE.Mesh(flagGeom, flagMat);
  flag.position.set(flagPoleX, 2.45, flagPoleZ + 0.35);
  flag.rotation.x = -0.5; // Tilt forward
  group.add(flag);

  // === LANTERNS ===
  const createLantern = (x: number, z: number) => {
    const cordGeom = new THREE.CylinderGeometry(0.008, 0.008, 0.12, 4);
    const cord = new THREE.Mesh(cordGeom, darkWoodMat);
    cord.position.set(x, 1.54, z);
    group.add(cord);

    const bodyGeom = new THREE.BoxGeometry(0.1, 0.14, 0.1);
    const body = new THREE.Mesh(bodyGeom, lanternMat);
    body.position.set(x, 1.42, z);
    group.add(body);

    const topGeom = new THREE.ConeGeometry(0.06, 0.04, 4);
    const topMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.8 });
    const top = new THREE.Mesh(topGeom, topMat);
    top.position.set(x, 1.52, z);
    top.rotation.y = Math.PI / 4;
    group.add(top);
  };

  createLantern(-0.85, 0.5);
  createLantern(0.85, 0.5);

  // === INTERIOR DECORATIONS ===
  // Bow rack on left wall
  const rackBarGeom = new THREE.BoxGeometry(0.04, 0.04, 0.35);
  const rackBar = new THREE.Mesh(rackBarGeom, woodMat);
  rackBar.position.set(-1.08, 0.7, -0.5);
  group.add(rackBar);

  // Bows on wall
  for (let i = 0; i < 2; i++) {
    const bowGeom = new THREE.TorusGeometry(0.12, 0.015, 6, 12, Math.PI);
    const bow = new THREE.Mesh(bowGeom, woodMat);
    bow.position.set(-1.0, 0.7, -0.35 - i * 0.3);
    bow.rotation.y = Math.PI / 2;
    bow.rotation.z = Math.PI / 2;
    group.add(bow);
  }

  // Quiver on right
  const quiverGeom = new THREE.CylinderGeometry(0.05, 0.06, 0.3, 8);
  const quiverMat = new THREE.MeshStandardMaterial({ color: 0x8b5a3a, roughness: 0.8 });
  const quiver = new THREE.Mesh(quiverGeom, quiverMat);
  quiver.position.set(1.0, 0.45, -0.5);
  quiver.rotation.z = 0.1;
  group.add(quiver);

  // Arrows
  for (let i = 0; i < 4; i++) {
    const arrowGeom = new THREE.CylinderGeometry(0.006, 0.006, 0.38, 4);
    const arrowMat = new THREE.MeshStandardMaterial({ color: 0x5d4037 });
    const arrow = new THREE.Mesh(arrowGeom, arrowMat);
    const angle = (i / 4) * Math.PI * 2;
    arrow.position.set(1.0 + Math.cos(angle) * 0.02, 0.6, -0.5 + Math.sin(angle) * 0.02);
    arrow.rotation.z = 0.1;
    group.add(arrow);
  }

  // Training mat
  const matGeom = new THREE.BoxGeometry(0.6, 0.02, 0.4);
  const matMat = new THREE.MeshStandardMaterial({ color: 0x9a7a4a, roughness: 0.9 });
  const mat = new THREE.Mesh(matGeom, matMat);
  mat.position.set(0, 0.27, -0.4);
  group.add(mat);

  // Small stool
  const stoolGeom = new THREE.BoxGeometry(0.2, 0.15, 0.2);
  const stool = new THREE.Mesh(stoolGeom, woodMat);
  stool.position.set(0.5, 0.32, -0.6);
  group.add(stool);
}
